import { useEffect, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import EditPermitTypeDocumentTypeSettingsForm, { FormData } from "./EditPermitTypeDocumentTypeSettingsForm";
import { FormError } from "../models/FormError";
import { PermitTypeDocumentType } from "../models/PermitTypeDocumentType";
import { DocumentRequirementKey } from "../models/globalTypes";
import { DocumentTypeSettingsInput } from "../models/globalTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (documentTypeId: string, documentTypeSettingsInput: DocumentTypeSettingsInput | null) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  permitTypeDocumentType?: PermitTypeDocumentType;
}

const defaultFormData: FormData = {
  useDefault: true,
  declaration: "",
  requirementKey: DocumentRequirementKey.NONE,
};

const EditPermitTypeDocumentTypeSettingsModal = ({
  open,
  onClose,
  onSubmit,
  submitInProgress,
  submitErrors,
  permitTypeDocumentType,
}: Props) => {
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (open && permitTypeDocumentType) {
      const hasOverride = permitTypeDocumentType.settings !== null;
      const src = hasOverride ? permitTypeDocumentType.settings! : permitTypeDocumentType.documentType.settings;
      setFormData({
        useDefault: !hasOverride,
        declaration: src.declaration,
        requirementKey: src.requirement.key,
      });
    } else {
      setFormData(defaultFormData);
    }
    setFormErrors({});
    setHasSubmitted(false);
  }, [open, permitTypeDocumentType]);

  const validateFormData = (_data: FormData): FormError => {
    const formError: FormError = {};
    return formError;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);

    const validationErrors = validateFormData(formData);
    setFormErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const settingsInput: DocumentTypeSettingsInput | null = formData.useDefault
        ? null
        : {
            declaration: formData.declaration,
            requirementKey: formData.requirementKey,
          };

      onSubmit(permitTypeDocumentType!.documentType.id, settingsInput);
    }
  };

  const handleFormChange = (data: FormData) => {
    if (hasSubmitted) {
      setFormErrors(validateFormData(data));
    }

    setFormData({ ...data });
  };

  return (
    <Modal open={open} size="small">
      <Modal.Header>Document type settings — {permitTypeDocumentType?.documentType.name}</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <EditPermitTypeDocumentTypeSettingsForm
            formData={formData}
            formErrors={formErrors}
            errors={submitErrors}
            onChange={handleFormChange}
          />
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} positive loading={submitInProgress} disabled={submitInProgress}>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditPermitTypeDocumentTypeSettingsModal;
